
import React, { useState, useRef, useEffect } from "react";
import "./TravelerSelector.css"
import { MenuItem, Select, FormControl, InputLabel, Box } from "@mui/material";
import minus from "../../assets/images/packages/svg/minus.svg"
import plus from "../../assets/images/packages/svg/plus.svg"
import { useTranslation } from "../../contexts/TranslationContext";
const TravelerSelector = ({ adults, 
  children, 
  childrenAges,
  infants,
  onAdultsChange,
  onChildrenChange,
  onChildrenAgesChange,
  onInfantsChange
 }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const { t } = useTranslation();


  // Toggle dropdown
  const handleOpen = () => setOpen(!open);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(event.target) && 
        !document.querySelector(".MuiPopover-root")?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Handle adult count (Min: 1, Max: 14)
  const handleAdultChange = (change) => {
    const newAdults = Math.min(14, Math.max(1, adults + change));
    onAdultsChange(newAdults);
  };

  // Handle children count (Min: 0, Max: 6)
  const handleChildrenChange = (change) => {
    const newChildren = Math.min(6, Math.max(0, children + change));
    onChildrenChange(newChildren);

    // Update children ages array
    const newAges = newChildren > childrenAges.length
      ? [...childrenAges, null]
      : childrenAges.slice(0, newChildren);
    onChildrenAgesChange(newAges);
  };

  // Handle child age selection
  const handleChildAgeChange = (index, age) => {
    const newAges = [...childrenAges];
    newAges[index] = age;
    onChildrenAgesChange(newAges);
  };

// Handle infants count (Min: 0, Max: 6)
const handleInfantsChange = (change) => {
  const newInfants = Math.min(6, Math.max(0, infants + change));
  onInfantsChange(newInfants);
};


  // Validate required age selection before closing
  const handleClose = () => {
    if (children > 0 && childrenAges.some((age) => age === null || age === "")) {
      setError(true);
    } else {
      setError(false);
      setOpen(false);
    }
  };

  return (
    <div className="traveler-container" ref={containerRef}>
      {/* Input Field */}
      <div className="traveler-input" onClick={handleOpen}>
        {t("Adults")} {adults} ,{t("Children")} {children},{t("Infants")} {infants}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="traveler-dropdown">
          <div className="mb-4">
            <div className="traveler-counter-container">
              <span className="traveler-name">{t("Adults")}</span>
              <div className="traveler-counter">
                <button
                  type="button"
                  className="traveler-counter-btn"
                  onClick={() => handleAdultChange(-1)}
                >
                  <img src={minus} alt="minus" />
                </button>
                <span>{adults}</span>
                <button
                  type="button"
                  className="traveler-counter-btn"
                  onClick={() => handleAdultChange(1)}
                >
                  <img src={plus} alt="plus" />
                </button>
              </div>
            </div>

                        <div className="traveler-counter-container">
              <div className="traveler-name">
                <span>{t("Infants")}</span>
                <span className="traveler-name-desc">{t("LessThan")} 2</span>
              </div>
              <div className="traveler-counter">
                <button
                  type="button"
                  className="traveler-counter-btn"
                  onClick={() => handleInfantsChange(-1)}
                >
                  <img src={minus} alt="minus" />
                </button>
                <span>{infants}</span>
                <button
                  type="button"
                  className="traveler-counter-btn"
                  onClick={() => handleInfantsChange(1)}
                >
                  <img src={plus} alt="plus" />
                </button>
              </div>
            </div>

            <div className="traveler-counter-container">
              <div className="traveler-name">
                <span>{t("Children")}</span>
                <span className="traveler-name-desc">
                  {t("Ages")} 2 {t("To")} 12
                </span>
              </div>
              <div className="traveler-counter">
                <button
                  type="button"
                  className="traveler-counter-btn"
                  onClick={() => handleChildrenChange(-1)}
                >
                  <img src={minus} alt="minus" />
                </button>
                <span>{children}</span>
                <button
                  type="button"
                  className="traveler-counter-btn"
                  onClick={() => handleChildrenChange(1)}
                >
                  <img src={plus} alt="plus" />
                </button>
              </div>
            </div>


          </div>

          {/* Child Age Dropdowns */}
          {children > 0 &&
            childrenAges &&
            childrenAges.map((age, index) => (
              <Box
                sx={{
                  marginTop: "15px",
                }}
              >
                <FormControl
                  fullWidth
                  error={error && (age === null || age === "")}
                >
                  <InputLabel className="child-select-label">
                    {t("Child")} {index + 1} {t("Age")} <span>*</span>
                  </InputLabel>
                  <Select
                    value={age ?? ""}
                    onChange={(e) =>
                      handleChildAgeChange(index, e.target.value)
                    }
                    required
                    label={t("ChildAge")}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "12px", // Reduce padding
                        fontSize: "14px", // Adjust font size
                        fontFamily: "Dmsans300", // Minimum width
                      },
                    }}
                  >
                    {[...Array(11).keys()].map((num) => (
                      <MenuItem key={num + 2} value={num + 2}>
                        {num + 2}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}

          {/* Error Message if Age is Missing */}
          {error && (
            <p className="traveler-error-msg">{t("ValidationChildrenAge")}</p>
          )}

          <button type="button" className="close-btn" onClick={handleClose}>
            {t("Done")}
          </button>
        </div>
      )}
    </div>
  );
};

export default TravelerSelector;
